import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, MessageCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import NavigationFeedback from '../../components/NavigationFeedback';
import ConnectionStatus from '../../components/ConnectionStatus';
import EmergencyButton from '../../components/EmergencyButton';
import { LanguagePicker } from '../../components/LanguagePicker';
import { AvatarMenu } from './AvatarMenu';
import { useTranslation } from 'react-i18next';

interface DashboardTopBarProps {
  pageTitle: string;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  unreadCount: number;
  unreadNotificationsCount?: number;
  onlineUsersCount: number;
  userType: 'patient' | 'doctor' | 'admin' | 'nurse' | 'clinic';
  avatarMenuOpen: boolean;
  onToggleAvatarMenu: () => void;
  onCloseAvatarMenu: () => void;
  avatarMenuRef: React.RefObject<HTMLDivElement>;
}

/**
 * Top navigation bar with notifications, connection status, and user menu
 */
export const DashboardTopBar: React.FC<DashboardTopBarProps> = ({
  pageTitle,
  sidebarOpen,
  onToggleSidebar,
  unreadCount,
  unreadNotificationsCount = 0,
  onlineUsersCount,
  userType,
  avatarMenuOpen,
  onToggleAvatarMenu,
  onCloseAvatarMenu,
  avatarMenuRef,
}) => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <header className="bg-white/60 backdrop-blur-3xl z-30 sticky top-0 shadow-[0_1px_10px_rgba(0,50,115,0.01)] transition-all duration-300">
      <div className="px-4 sm:px-10 lg:px-12 py-4 sm:py-5 flex items-center justify-between">
        {/* Left: Menu toggle and page title */}
        <div className="flex items-center">
          <button
            className="lg:hidden mr-2 sm:mr-4 text-slate-500 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-xl p-2 hover:bg-white/50 transition-all"
            onClick={onToggleSidebar}
            aria-label={t('topbar.openNavigation')}
            aria-expanded={sidebarOpen ? 'true' : 'false'}
            aria-controls="sidebar-navigation"
          >
            <Menu size={24} />
          </button>
          <div className="flex flex-col ml-1 sm:ml-2">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black bg-gradient-to-r from-med-primary via-[#004a99] to-slate-500 bg-clip-text text-transparent tracking-tighter uppercase italic leading-none">
              {pageTitle}
            </h1>
            <div className="h-1 w-12 bg-gradient-to-r from-med-success to-transparent rounded-full mt-1.5 opacity-80" />
          </div>
        </div>

        {/* Right: Status, notifications, and user menu */}
        <div className="flex items-center space-x-3 sm:space-x-5">
          {/* Navigation feedback */}
          <NavigationFeedback className="hidden lg:flex" />

          {/* Connection status */}
          <ConnectionStatus className="hidden sm:inline-flex" />

          {/* Online users count - Elite Version */}
          <div className="hidden md:flex items-center gap-2.5 px-4 py-2 bg-emerald-500/10 rounded-2xl shadow-[0_0_15px_rgba(16,185,129,0.03)] group hover:bg-emerald-500/15 transition-all cursor-default">
            <div className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </div>
            <span className="text-xs font-bold tracking-widest text-emerald-700 uppercase">
              {t('topbar.onlinePractitioners', { count: onlineUsersCount })}
            </span>
          </div>

          {/* Message notification bubble */}
          <button
            className="relative p-2 text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full hover:bg-slate-100 transition-all"
            onClick={() => navigate(`/${userType}/messages`)}
            title={t('topbar.unreadMessages', { count: unreadCount })}
            aria-label={t('topbar.unreadMessages', { count: unreadCount })}
          >
            {/* MessageCircle icon with animation */}
            <MessageCircle
              size={22}
              className={unreadCount > 0 ? 'animate-pulse text-blue-600' : ''}
              aria-hidden="true"
            />

            {/* Unread count badge Elite */}
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-gradient-to-br from-blue-500 to-blue-600 text-white text-[10px] font-black rounded-full flex items-center justify-center animate-pulse shadow-[0_0_15px_rgba(59,130,246,0.5)] z-20"
                aria-label={t('topbar.unreadMessages', { count: unreadCount })}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Medical alerts & scheduling notification bell */}
          <button
            className="relative p-2 text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full hover:bg-slate-100 transition-all"
            onClick={() => navigate(`/notifications`)}
            title={t('topbar.unreadNotifications', { count: unreadNotificationsCount })}
            aria-label={t('topbar.unreadNotifications', { count: unreadNotificationsCount })}
          >
            {/* Bell icon with animation */}
            <Bell
              size={22}
              className={unreadNotificationsCount > 0 ? 'animate-bell-ring text-red-500' : ''}
              aria-hidden="true"
            />

            {/* Unread notifications badge Elite */}
            {unreadNotificationsCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px] font-black rounded-full flex items-center justify-center animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.5)] z-20"
                aria-label={t('topbar.unreadNotifications', { count: unreadNotificationsCount })}
              >
                {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
              </span>
            )}
          </button>

          {/* Emergency button */}
          <EmergencyButton />

          {/* Language picker */}
          <LanguagePicker />

          {/* Avatar menu */}
          {user && (
            <AvatarMenu
              user={user}
              isOpen={avatarMenuOpen}
              onToggle={onToggleAvatarMenu}
              onClose={onCloseAvatarMenu}
              // ✅ SÉCURITÉ: Utiliser userType (chemin du layout) et non user.role
              // évite /clinic_manager/profile → 404 pour les CLINIC_MANAGER
              onProfile={() => navigate(`/${userType}/profile`)}
              onSettings={() => navigate(`/${userType}/settings`)}
              onLogout={logout}
              menuRef={avatarMenuRef}
            />
          )}
        </div>
      </div>
    </header>
  );
};
